/**
 * AppDataContext — the single in-memory domain store the ported UI reads from.
 *
 * Wave 3: the dev-seed bootstrap is replaced by live API loads via `lib/apiClient`.
 * Pages are untouched — they still call `useData()` for the data bag and
 * `useDataActions()` for the mutators (the same surface the prototype's
 * `window.HERO_DATA` + App-level callbacks gave).
 *
 * Loading model
 * -------------
 * `AppDataProvider` sits ABOVE `AuthProvider` in the tree, so it cannot read the
 * auth context. Instead it resolves the session itself via `apiClient.auth.me()`:
 * on mount it probes once; while unauthenticated it re-probes on a short interval
 * (cleared the moment a session appears). When authenticated it loads
 * projects + users + pricing + (per project) timeline/activity/quotations/
 * compensation/documents/meetings into the same `HeroData` shape pages consume.
 *
 * `categories` and `months2026` are pure UI constants (no API resource) and are
 * taken from the dev-seed. `team` is derived from `GET /api/users`. Everything
 * else is live. Setting `VITE_USE_SEED=1` falls back to the static dev-seed
 * (no network) for offline UI work.
 *
 * Mutators map to the API and optimistically update local state, rolling back on
 * failure. They are async; pages that want success/error toasts can await them.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import type {
  TimelineItem, TaskStatus, Compensation, CompensationGroup, Project, Quotation, ActivityLog, User, CatalogItem,
  DocumentMeta, DataManagementItem, AnalysisFormula,
} from '@heros/shared';
import { DEFAULT_ANALYSIS_FORMULA } from '@heros/shared';
import { createSeed } from '../lib/seed';
import type { HeroData } from '../lib/seed';
import {
  apiClient, ApiClientError,
  type CompensationView,
  type MeetingWriteBody,
  type MeetingWriteResult,
} from '../lib/apiClient';

const USE_SEED = (import.meta.env.VITE_USE_SEED as string | undefined) === '1';

function nowStamp(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10) + ' ' + d.toTimeString().slice(0, 5);
}

export interface CreateProjectInput {
  form: any;
  quotation: { sections: any[]; grandTotal: number; meta?: any };
}

export interface DataActions {
  createProjectFromBuilder: (input: CreateProjectInput, resumeProjectId?: string | null) =>
    Promise<{ projectId: string }>;
  addTimelineItem: (item: TimelineItem) => Promise<void>;
  updateTimeline: (item: TimelineItem) => Promise<void>;
  /** Persist a new row order for a set of timeline items (drag-to-reorder). */
  reorderTimeline: (orderedIds: string[]) => Promise<void>;
  deleteTimeline: (id: string) => Promise<void>;
  updateTimelineStatus: (id: string, status: TaskStatus) => Promise<void>;
  /** Patch a project's editable profile fields. */
  updateProject: (id: string, patch: Partial<Project>) => Promise<void>;
  /** Step an ACTIVE project back to draft (provisional); acceptance is reset. */
  revertToDraft: (projectId: string) => Promise<void>;
  /** Soft-delete: moves the project to the recycle bin (restorable 30 days). */
  deleteProject: (projectId: string) => Promise<void>;
  /** Restore a soft-deleted project from the recycle bin. */
  restoreProject: (projectId: string) => Promise<void>;
  /** Add a user to a project's members. */
  addProjectMember: (projectId: string, userId: string) => Promise<void>;
  /** Remove a user from a project's members. */
  removeProjectMember: (projectId: string, userId: string) => Promise<void>;
  approveUser: (id: string) => void;
  rejectUser: (id: string) => void;
  /** Upload a document to a project (multipart), then refresh that project's docs. */
  uploadDocument: (input: { projectId: string; type: string; status?: DocumentMeta['status']; category?: string; description?: string; version?: string; file: File }) => Promise<DocumentMeta>;
  /** Delete a document version; promotes the newest remaining version when needed. */
  deleteDocument: (id: string) => Promise<void>;
  /** Create a meeting log / appointment linked to a project. */
  createMeeting: (input: MeetingWriteBody & { projectId: string; title: string; date: string }) => Promise<MeetingWriteResult>;
  /** Edit an existing meeting log / appointment. */
  updateMeeting: (id: string, input: MeetingWriteBody) => Promise<MeetingWriteResult>;
  updateComp: (comp: Compensation & { isNew?: boolean }) => Promise<void>;
  /** Persist a new manual order for a project's compensation rows (drag-to-reorder). */
  reorderCompensation: (orderedIds: string[]) => Promise<void>;
  /** Delete a compensation row (+ its splits). */
  deleteCompensation: (id: string) => Promise<void>;
  toggleReceived: (compId: string, userId: string) => Promise<void>;
  /** Compensation GROUPS (3-level money model). */
  createCompGroup: (projectId: string, name: string, groupPct?: number) => Promise<CompensationGroup>;
  updateCompGroup: (id: string, patch: { name?: string; groupPct?: number }) => Promise<void>;
  deleteCompGroup: (id: string) => Promise<void>;
  reorderCompGroups: (projectId: string, orderedIds: string[]) => Promise<void>;
  /** Move a task to a group (null → ungroup) / set its within-group %. */
  moveCompToGroup: (compId: string, groupId: string | null, groupTaskPct?: number | null) => Promise<void>;
  /** Ensure the default groups + bucket tasks + even-distribute (new/no-money projects). */
  seedDefaultGroups: (projectId: string) => Promise<void>;
  /** Catalog (lab/procedure/variable library + promotion queue). */
  proposeCatalogItem: (input: Parameters<typeof apiClient.catalog.propose>[0]) => Promise<CatalogItem>;
  /** Admin direct-create: lands an ACTIVE library item in one step. */
  createCatalogItem: (input: Parameters<typeof apiClient.catalog.create>[0]) => Promise<CatalogItem>;
  approveCatalogItem: (id: string, edits?: Parameters<typeof apiClient.catalog.approve>[1]) => Promise<CatalogItem>;
  rejectCatalogItem: (id: string, reviewNote?: string) => Promise<CatalogItem>;
  updateCatalogItem: (id: string, edits: Parameters<typeof apiClient.catalog.update>[1]) => Promise<CatalogItem>;
  removeCatalogItem: (id: string) => Promise<void>;
  /** DM cost catalog (DMP/CRF databank) — admin edit/add/remove. */
  createDmItem: (body: Partial<DataManagementItem>) => Promise<DataManagementItem>;
  updateDmItem: (id: string, body: Partial<DataManagementItem>) => Promise<DataManagementItem>;
  removeDmItem: (id: string) => Promise<void>;
  /** Force a full reload from the API (used after out-of-band changes). */
  reload: () => Promise<void>;
  /** Register a toast sink so mutators can surface 400/403 errors. */
  setErrorSink: (fn: ((msg: string) => void) | null) => void;
}

interface AppDataValue {
  data: HeroData;
  actions: DataActions;
  /** Whether the live data has finished its first load (or seed is ready). */
  ready: boolean;
  /** Acting user id — drives "View as" + stamps on mutations. */
  currentUserId: string;
  setCurrentUserId: (id: string) => void;
}

const Ctx = createContext<AppDataValue | null>(null);

/* ------------------------------------------------------------------ *
 * Compensation mapping. The list/single endpoints return the base DTO
 * plus `splitStates` (keyed by split id, in split order). The DTO's
 * splits[] carry no id, so we zip the splitStates keys onto the splits by
 * index to recover each split's server id — needed for the per-split
 * "received" toggle.
 * ------------------------------------------------------------------ */
type SplitIdMap = Record<string, string>; // `${compId}:${userId}` -> splitId

function indexCompSplitIds(views: CompensationView[]): SplitIdMap {
  const map: SplitIdMap = {};
  for (const c of views) {
    const ids = c.splitStates ? Object.keys(c.splitStates) : [];
    c.splits.forEach((s, i) => {
      const splitId = ids[i];
      if (splitId) map[`${c.id}:${s.user}`] = splitId;
    });
  }
  return map;
}

/** Strip the API-only fields back to the plain `Compensation` DTO pages expect.
 *  MUST carry allocPct + groupId + groupTaskPct, or the 3-level grouping (and the
 *  legacy %) silently evaporate on every reload (write-only bug). */
function toCompDTO(v: CompensationView): Compensation {
  return {
    id: v.id, taskId: v.taskId, projectId: v.projectId, label: v.label, base: v.base, note: v.note, order: v.order,
    allocPct: v.allocPct, groupId: v.groupId, groupTaskPct: v.groupTaskPct,
    splits: v.splits.map((s) => ({ ...s })),
  };
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  // Static UI scaffolding (categories palette + month labels) — no API resource.
  const [scaffold] = useState(() => {
    const s = createSeed();
    return { months2026: s.months2026, categories: s.categories };
  });

  // Live collections.
  const [team, setTeam] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [compensations, setCompensations] = useState<Compensation[]>([]);
  const [compensationGroups, setCompensationGroups] = useState<CompensationGroup[]>([]);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [documents, setDocuments] = useState<HeroData['documents']>([]);
  const [meetings, setMeetings] = useState<HeroData['meetings']>([]);
  const [pendingUsers, setPendingUsers] = useState<HeroData['pendingUsers']>([]);
  const [pricingProtocol, setPricingProtocol] = useState<HeroData['pricingProtocol']>([]);
  const [pricingDescriptive, setPricingDescriptive] = useState<HeroData['pricingDescriptive']>([]);
  const [pricingTopup, setPricingTopup] = useState<HeroData['pricingTopup']>([]);
  const [catalog, setCatalog] = useState<HeroData['catalog']>([]);
  const [dmCatalog, setDmCatalog] = useState<HeroData['dmCatalog']>([]);
  const [analysisFormula, setAnalysisFormula] = useState<AnalysisFormula>(DEFAULT_ANALYSIS_FORMULA);

  const [ready, setReady] = useState(USE_SEED);
  const [currentUserId, setCurrentUserId] = useState('u1');

  // splitId lookup for the per-split "received" toggle.
  const splitIdMap = useRef<SplitIdMap>({});
  // toast sink (registered by Shell once the ToastProvider is mounted).
  const errorSink = useRef<((msg: string) => void) | null>(null);
  const reportError = useCallback((err: unknown, fallback: string) => {
    const msg = err instanceof ApiClientError ? (err.body?.message ?? fallback) : fallback;
    if (errorSink.current) errorSink.current(msg);
    else if (typeof console !== 'undefined') console.error(msg, err);
  }, []);

  /* ---------------- Full load from the API ---------------- */
  const loadAll = useCallback(async () => {
    if (USE_SEED) { setReady(true); return; }
    // Users (team) + projects + pricing in parallel.
    // GET /api/users is admin-only (sys.users.manage); for non-admin roles it 403s,
    // so we fall back to GET /api/users/roster (requireAuth — every authenticated user)
    // which returns the real id/name/initials/role for ALL active users. Only if even
    // that fails do we use the static seed. This keeps post-seed members rendering +
    // their profiles clickable for non-admins (senior request: everyone views everyone).
    const [users, projectList, pricing, catalogList, dmCatalogList, formula] = await Promise.all([
      apiClient.users.list().catch(() => apiClient.users.roster().then((r) => r as unknown as User[]).catch(() => [] as User[])),
      apiClient.projects.list(),
      apiClient.pricing.get().catch(() => ({ protocol: [], descriptive: [], topup: [] })),
      apiClient.catalog.list().catch(() => []),
      apiClient.dmCatalog.list().catch(() => []),
      apiClient.analysisFormula.get().catch(() => DEFAULT_ANALYSIS_FORMULA),
    ]);
    setTeam(users.length ? users : createSeed().team);
    setProjects(projectList);
    setPricingProtocol(pricing.protocol);
    setPricingDescriptive(pricing.descriptive);
    setPricingTopup(pricing.topup);
    setCatalog(catalogList);
    setDmCatalog(dmCatalogList);
    setAnalysisFormula(formula);

    // Per-project collections (timeline/quotation/compensation/activity/docs/meetings).
    const ids = projectList.map((p) => p.id);
    const [tl, qs, comps, compGroups, acts, docs, mtgs] = await Promise.all([
      Promise.all(ids.map((id) => apiClient.timeline.list(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.quotations.list(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.compensation.list(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.compensation.groups.list(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.activity.list(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.projects.documents(id).catch(() => []))),
      Promise.all(ids.map((id) => apiClient.projects.meetings(id).catch(() => []))),
    ]);
    setTimeline(tl.flat());
    setQuotations(qs.flat());
    const compViews = comps.flat();
    splitIdMap.current = indexCompSplitIds(compViews);
    setCompensations(compViews.map(toCompDTO));
    setCompensationGroups(compGroups.flat());
    setLogs(acts.flat());
    setDocuments(docs.flat());
    setMeetings(mtgs.flat());

    // Pending users — admin-only; ignore 403.
    try { setPendingUsers(await apiClient.users.pending()); } catch { /* not an admin */ }

    setReady(true);
  }, []);

  // Re-fetch just the activity feed for one project (after a status write).
  const refreshActivity = useCallback(async (projectId: string) => {
    try {
      const acts = await apiClient.activity.list(projectId);
      setLogs((prev) => [...acts, ...prev.filter((l) => l.project !== projectId)]);
    } catch { /* non-fatal */ }
  }, []);

  // Re-fetch compensations + groups for one project (keeps splitId map + group
  // bases fresh — server recompute may have re-derived many rows at once).
  const refreshComp = useCallback(async (projectId: string) => {
    try {
      const [views, groups] = await Promise.all([
        apiClient.compensation.list(projectId),
        apiClient.compensation.groups.list(projectId).catch(() => [] as CompensationGroup[]),
      ]);
      Object.assign(splitIdMap.current, indexCompSplitIds(views));
      setCompensations((prev) => [...prev.filter((c) => c.projectId !== projectId), ...views.map(toCompDTO)]);
      setCompensationGroups((prev) => [...prev.filter((g) => g.projectId !== projectId), ...groups]);
    } catch { /* non-fatal */ }
  }, []);

  /* ---------------- Auth-aware bootstrap ---------------- */
  useEffect(() => {
    if (USE_SEED) {
      // Offline dev: hydrate from the static seed.
      const s = createSeed();
      setTeam(s.team); setProjects(s.projects); setTimeline(s.timeline);
      setQuotations(s.quotations); setCompensations(s.compensations); setCompensationGroups(s.compensationGroups); setLogs(s.logs);
      setDocuments(s.documents); setMeetings(s.meetings); setPendingUsers(s.pendingUsers);
      setPricingProtocol(s.pricingProtocol); setPricingDescriptive(s.pricingDescriptive);
      setPricingTopup(s.pricingTopup); setReady(true);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let loaded = false;

    let inFlight = false;
    const tryLoad = async () => {
      if (loaded || cancelled || inFlight) return;
      inFlight = true;
      try {
        const me = await apiClient.auth.me();
        if (cancelled) return;
        setCurrentUserId(me.user.id);
        await loadAll();
        if (cancelled) return;
        // Mark loaded only AFTER a full successful load — declaring success
        // before loadAll used to strand the app (timer already cleared, no
        // retry, setReady never reached) when one of the loads failed.
        loaded = true;
        if (timer) { clearInterval(timer); timer = null; }
      } catch {
        // 401 / network / load failure: stay idle and let the interval re-probe.
      } finally {
        inFlight = false;
      }
    };

    void tryLoad();
    timer = setInterval(() => { void tryLoad(); }, 1200);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [loadAll]);

  const data: HeroData = useMemo(() => ({
    months2026: scaffold.months2026,
    categories: scaffold.categories,
    team, projects, timeline, quotations, compensations, compensationGroups, logs,
    documents, meetings, pendingUsers,
    pricingProtocol, pricingDescriptive, pricingTopup, catalog, dmCatalog, analysisFormula,
  }), [scaffold, team, projects, timeline, quotations, compensations, compensationGroups, logs,
    documents, meetings, pendingUsers, pricingProtocol, pricingDescriptive, pricingTopup, catalog, dmCatalog, analysisFormula]);

  /* ---------------- Mutators ---------------- */

  // The server auto-syncs Project.status from the timeline after every
  // timeline write — pull the fresh project row so the UI follows. Best-effort.
  const refreshProject = useCallback(async (projectId: string) => {
    try {
      const fresh = await apiClient.projects.get(projectId);
      setProjects((ps) => ps.map((p) => (p.id === fresh.id ? fresh : p)));
    } catch { /* non-fatal */ }
  }, []);

  // Re-fetch one project's timeline rows (the comp↔timeline live link rewrites
  // splitsJson server-side, so the other side must refetch to show the mirror).
  const refreshTimeline = useCallback(async (projectId: string) => {
    try {
      const rows = await apiClient.projects.timeline(projectId);
      setTimeline((prev) => [...prev.filter((t) => t.project !== projectId), ...rows]);
    } catch { /* non-fatal */ }
  }, []);

  const addTimelineItem = useCallback(async (item: TimelineItem) => {
    try {
      const created = await apiClient.timeline.create({
        project: item.project, name: item.name, category: item.category, user: item.user,
        ...(item.rowType ? { rowType: item.rowType } : {}),
        ...(item.durationWeeks !== undefined ? { durationWeeks: item.durationWeeks } : {}),
        ...(item.order !== undefined ? { order: item.order } : {}),
        start: item.start, end: item.end, startDate: item.startDate, endDate: item.endDate,
        status: item.status,
      });
      setTimeline((t) => [...t, created]);
      void refreshProject(item.project);
    } catch (err) {
      reportError(err, 'Could not add the timeline item.');
    }
  }, [refreshProject, reportError]);

  const updateTimeline = useCallback(async (item: TimelineItem) => {
    const prev = timeline;
    setTimeline((t) => t.map((x) => (x.id === item.id ? item : x))); // optimistic
    try {
      const saved = await apiClient.timeline.update(item.id, {
        name: item.name, category: item.category, user: item.user,
        ...(item.splits !== undefined ? { splits: item.splits } : {}),
        ...(item.durationWeeks !== undefined ? { durationWeeks: item.durationWeeks } : {}),
        start: item.start, end: item.end, startDate: item.startDate, endDate: item.endDate,
        status: item.status,
      });
      setTimeline((t) => t.map((x) => (x.id === saved.id ? saved : x)));
      void refreshActivity(item.project);
      void refreshProject(item.project);
      // Two-way link: a responsibility-split edit mirrors into compensation server-side.
      if (item.splits !== undefined) void refreshComp(item.project);
    } catch (err) {
      setTimeline(prev); // rollback
      reportError(err, 'Could not save the timeline item.');
    }
  }, [timeline, refreshActivity, refreshProject, refreshComp, reportError]);

  // Persist drag-to-reorder by writing each row's `order` index (the list endpoint
  // sorts by order). Optimistic; rolls back on failure.
  const reorderTimeline = useCallback(async (orderedIds: string[]) => {
    const prev = timeline;
    const idSet = new Set(orderedIds);
    setTimeline((t) => {
      const byId = new Map(t.map((x) => [x.id, x]));
      const reordered = orderedIds.map((id, i) => { const x = byId.get(id); return x ? { ...x, order: i } : null; }).filter(Boolean) as TimelineItem[];
      const others = t.filter((x) => !idSet.has(x.id));
      return [...others, ...reordered];
    });
    try {
      await Promise.all(orderedIds.map((id, i) => apiClient.timeline.update(id, { order: i })));
      // The concurrent PATCHes each re-sync the project status against a
      // half-reordered list; one final no-op write settles the sync on the
      // finished order, then pull the fresh project row.
      const first = timeline.find((t) => t.id === orderedIds[0]);
      if (first) {
        await apiClient.timeline.update(orderedIds[0], { order: 0 }).catch(() => undefined);
        void refreshProject(first.project);
      }
    } catch (err) {
      setTimeline(prev);
      reportError(err, 'Could not save the new task order.');
    }
  }, [timeline, refreshProject, reportError]);

  const deleteTimeline = useCallback(async (id: string) => {
    const prev = timeline;
    const task = timeline.find((x) => x.id === id);
    setTimeline((t) => t.filter((x) => x.id !== id)); // optimistic
    try {
      await apiClient.timeline.remove(id);
      if (task) void refreshProject(task.project);
    } catch (err) {
      setTimeline(prev);
      reportError(err, 'Could not delete the timeline item.');
    }
  }, [timeline, refreshProject, reportError]);

  const updateTimelineStatus = useCallback(async (id: string, status: TaskStatus) => {
    const prev = timeline;
    const task = timeline.find((x) => x.id === id);
    // optimistic stamp
    setTimeline((t) => t.map((x) => (x.id === id
      ? { ...x, status, lastUpdate: { by: currentUserId, at: nowStamp(), status } }
      : x)));
    try {
      const saved = await apiClient.timeline.setStatus(id, status);
      setTimeline((t) => t.map((x) => (x.id === saved.id ? saved : x)));
      if (task) {
        // Server triple-writes ActivityLog; pull the fresh feed + comp states
        // + the project row (its status is auto-synced from the timeline).
        void refreshActivity(task.project);
        void refreshComp(task.project);
        void refreshProject(task.project);
      }
    } catch (err) {
      setTimeline(prev);
      reportError(err, 'Could not update the task status.');
    }
  }, [timeline, currentUserId, refreshActivity, refreshComp, refreshProject, reportError]);

  const updateProject = useCallback(async (id: string, patch: Partial<Project>) => {
    const prev = projects;
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p))); // optimistic
    try {
      const saved = await apiClient.projects.update(id, patch);
      setProjects((ps) => ps.map((p) => (p.id === saved.id ? saved : p)));
      // The compensation money fields trigger a server-side recompute of every
      // %-driven row's base — pull the fresh rows so the table/totals match the
      // already-fresh remainder summary (server returns only the project).
      if (patch.actualReceived !== undefined || patch.centralFundPct !== undefined) {
        await refreshComp(id);
      }
    } catch (err) {
      setProjects(prev);
      reportError(err, 'Could not save the project.');
      throw err;
    }
  }, [projects, refreshComp, reportError]);

  const revertToDraft = useCallback(async (projectId: string) => {
    const proj = projects.find((p) => p.id === projectId);
    if (!proj) return;
    // Reset the snapshot's acceptance too, so the builder honestly re-runs the
    // Acceptance step instead of showing the old accepted state.
    let builderJson: string | undefined;
    if (proj.builderJson) {
      try {
        const bj = JSON.parse(proj.builderJson);
        bj.agreement = { ...(bj.agreement || {}), accepted: false, acceptedAt: '' };
        builderJson = JSON.stringify(bj);
      } catch { /* unreadable snapshot — lifecycle fields still revert */ }
    }
    try {
      const saved = await apiClient.projects.update(projectId, {
        provisional: true,
        status: 'Quotation preparation',
        // Drop any manual pin so a later re-activation resumes the
        // date-driven auto status.
        statusOverride: null,
        ...(builderJson ? { builderJson } : {}),
      } as Partial<Project>);
      setProjects((ps) => ps.map((p) => (p.id === saved.id ? saved : p)));
    } catch (err) {
      reportError(err, 'Could not move the project back to draft.');
      throw err;
    }
  }, [projects, reportError]);

  const deleteProject = useCallback(async (projectId: string) => {
    try {
      await apiClient.projects.remove(projectId);
      // Purge the project AND its collections from local state so dashboards
      // stop showing its tasks/quotations immediately (DB rows are kept for
      // the 30-day recovery window).
      setProjects((ps) => ps.filter((p) => p.id !== projectId));
      setTimeline((ts) => ts.filter((t) => t.project !== projectId));
      setQuotations((qs) => qs.filter((q) => q.projectId !== projectId));
      setCompensations((cs) => cs.filter((c) => c.projectId !== projectId));
      setLogs((ls) => ls.filter((l) => l.project !== projectId));
      setDocuments((ds) => ds.filter((d) => d.project !== projectId));
      setMeetings((ms) => ms.filter((m) => m.project !== projectId));
    } catch (err) {
      reportError(err, "You don't have permission to delete this project.");
      throw err;
    }
  }, [reportError]);

  const restoreProject = useCallback(async (projectId: string) => {
    try {
      await apiClient.projects.restore(projectId);
      await loadAll(); // bring the project AND its collections back into state
    } catch (err) {
      reportError(err, 'Could not restore the project.');
      throw err;
    }
  }, [loadAll, reportError]);

  const addProjectMember = useCallback(async (projectId: string, userId: string) => {
    try {
      const saved = await apiClient.projects.addMember(projectId, userId);
      setProjects((ps) => ps.map((p) => (p.id === saved.id ? saved : p)));
    } catch (err) {
      reportError(err, 'Could not add the member.');
      throw err;
    }
  }, [reportError]);

  const removeProjectMember = useCallback(async (projectId: string, userId: string) => {
    const prev = projects;
    setProjects((ps) => ps.map((p) => (p.id === projectId ? { ...p, members: p.members.filter((m) => m !== userId) } : p))); // optimistic
    try {
      const saved = await apiClient.projects.removeMember(projectId, userId);
      setProjects((ps) => ps.map((p) => (p.id === saved.id ? saved : p)));
    } catch (err) {
      setProjects(prev);
      reportError(err, 'Could not remove the member.');
      throw err;
    }
  }, [projects, reportError]);

  const uploadDocument = useCallback(async (input: { projectId: string; type: string; status?: DocumentMeta['status']; category?: string; description?: string; version?: string; file: File }) => {
    try {
      const saved = await apiClient.documents.upload(input);
      // Re-fetch this project's docs so prior-version archiving is reflected too.
      const docs = await apiClient.documents.list(input.projectId);
      setDocuments((prev) => [...prev.filter((d) => d.project !== input.projectId), ...docs]);
      return saved;
    } catch (err) {
      reportError(err, 'Could not upload the document.');
      throw err;
    }
  }, [reportError]);

  const deleteDocument = useCallback(async (id: string) => {
    const doc = documents.find((d) => d.id === id);
    try {
      await apiClient.documents.remove(id);
      // Re-fetch the affected project's docs so a promoted version reflects too.
      if (doc) {
        const docs = await apiClient.documents.list(doc.project);
        setDocuments((prev) => [...prev.filter((d) => d.project !== doc.project), ...docs]);
      } else {
        setDocuments((prev) => prev.filter((d) => d.id !== id));
      }
    } catch (err) {
      reportError(err, 'Could not delete the document.');
      throw err;
    }
  }, [documents, reportError]);

  const createMeeting = useCallback(async (input: MeetingWriteBody & { projectId: string; title: string; date: string }) => {
    try {
      const saved = await apiClient.meetings.create(input);
      // Keep the transient mailResult OUT of the stored DTO (server lists never
      // carry it) — return the full result so the caller can toast the outcome.
      const { mailResult, ...meeting } = saved;
      void mailResult;
      setMeetings((prev) => [meeting, ...prev]);
      return saved;
    } catch (err) {
      reportError(err, 'Could not save the meeting.');
      throw err;
    }
  }, [reportError]);

  const updateMeeting = useCallback(async (id: string, input: MeetingWriteBody): Promise<MeetingWriteResult> => {
    try {
      const saved = await apiClient.meetings.update(id, input);
      const { mailResult, ...meeting } = saved;
      void mailResult;
      setMeetings((prev) => prev.map((m) => (m.id === meeting.id ? meeting : m)));
      return saved;
    } catch (err) {
      reportError(err, 'Could not update the meeting.');
      throw err;
    }
  }, [reportError]);

  const approveUser = useCallback((id: string) => {
    // Admin.tsx drives approve/reject directly via apiClient.users; here we just
    // drop the row from the local pending list so any cached view stays in sync.
    setPendingUsers((p) => p.filter((u) => u.id !== id));
  }, []);
  const rejectUser = useCallback((id: string) => {
    setPendingUsers((p) => p.filter((u) => u.id !== id));
  }, []);

  const updateComp = useCallback(async (comp: Compensation & { isNew?: boolean; taskId?: string }) => {
    // (Re)allocate a compensation's splits. Σpct must equal 100 (server-enforced).
    // For a brand-new row (Feature 12), CREATE the compensation first, then allocate.
    try {
      let compId = comp.id;
      let projectId = comp.projectId;
      if (comp.isNew) {
        const created = await apiClient.compensation.create({
          taskId: comp.taskId || comp.id, label: comp.label, base: comp.base,
          allocPct: comp.allocPct ?? null, groupId: comp.groupId ?? null, groupTaskPct: comp.groupTaskPct ?? null,
        });
        compId = created.id;
        projectId = created.projectId;
        Object.assign(splitIdMap.current, indexCompSplitIds([created]));
      }
      await apiClient.compensation.allocateSplits(compId, {
        base: comp.base,
        note: comp.note,
        allocPct: comp.allocPct ?? null,
        groupId: comp.groupId ?? null,
        groupTaskPct: comp.groupTaskPct ?? null,
        splits: comp.splits.map((s) => ({
          user: s.user, pct: s.pct,
          overrideAmount: s.overrideAmount, overrideReason: s.overrideReason,
          received: s.received, receivedOn: s.receivedOn || undefined,
        })),
      });
      // The server recompute may re-derive sibling bases (group reconciliation), so
      // refresh the whole project rather than patching the single returned row.
      await refreshComp(projectId);
      // Two-way link: the people-split mirrors back onto the linked timeline task.
      void refreshTimeline(projectId);
    } catch (err) {
      reportError(err, 'Could not save the allocation (splits must total 100%).');
      throw err; // let the page keep the editor open
    }
  }, [reportError, refreshComp, refreshTimeline]);

  /* ---- Compensation GROUPS (3-level money model) ---- */
  const createCompGroup = useCallback(async (projectId: string, name: string, groupPct?: number) => {
    try {
      const g = await apiClient.compensation.groups.create({ projectId, name, groupPct });
      setCompensationGroups((gs) => [...gs, g]);
      return g;
    } catch (err) { reportError(err, 'Could not create the group.'); throw err; }
  }, [reportError]);

  const updateCompGroup = useCallback(async (id: string, patch: { name?: string; groupPct?: number }) => {
    const grp = compensationGroups.find((g) => g.id === id);
    try {
      const g = await apiClient.compensation.groups.update(id, patch);
      setCompensationGroups((gs) => gs.map((x) => (x.id === id ? g : x)));
      // A groupPct change re-sizes the pot → server recomputed member bases.
      if (patch.groupPct !== undefined && grp) await refreshComp(grp.projectId);
    } catch (err) { reportError(err, 'Could not update the group.'); throw err; }
  }, [compensationGroups, reportError, refreshComp]);

  const deleteCompGroup = useCallback(async (id: string) => {
    try {
      await apiClient.compensation.groups.remove(id);
      setCompensationGroups((gs) => gs.filter((g) => g.id !== id));
    } catch (err) { reportError(err, 'Could not delete the group (move its tasks out first).'); throw err; }
  }, [reportError]);

  const reorderCompGroups = useCallback(async (_projectId: string, orderedIds: string[]) => {
    const prev = compensationGroups;
    const pos = new Map(orderedIds.map((id, i) => [id, i]));
    setCompensationGroups((gs) => gs.map((g) => (pos.has(g.id) ? { ...g, order: pos.get(g.id)! } : g)));
    try { await apiClient.compensation.groups.reorder(orderedIds); }
    catch (err) { setCompensationGroups(prev); reportError(err, 'Could not reorder groups.'); }
  }, [compensationGroups, reportError]);

  const moveCompToGroup = useCallback(async (compId: string, groupId: string | null, groupTaskPct?: number | null) => {
    const comp = compensations.find((c) => c.id === compId);
    try {
      await apiClient.compensation.move(compId, { groupId, ...(groupTaskPct !== undefined ? { groupTaskPct } : {}) });
      if (comp) await refreshComp(comp.projectId);
    } catch (err) { reportError(err, 'Could not move the task.'); throw err; }
  }, [compensations, reportError, refreshComp]);

  const seedDefaultGroups = useCallback(async (projectId: string) => {
    try {
      await apiClient.compensation.groups.seedDefaults(projectId);
      await refreshComp(projectId);
    } catch (err) { reportError(err, 'Could not set up default groups.'); throw err; }
  }, [reportError, refreshComp]);

  // Drag-to-reorder the compensation rows (mirrors reorderTimeline). Optimistic;
  // rolls back on failure. Each id's `order` becomes its index in the new order.
  const reorderCompensation = useCallback(async (orderedIds: string[]) => {
    const prev = compensations;
    const pos = new Map(orderedIds.map((id, i) => [id, i]));
    setCompensations((cs) => cs.map((c) => (pos.has(c.id) ? { ...c, order: pos.get(c.id)! } : c)));
    try {
      await apiClient.compensation.reorder(orderedIds);
    } catch (err) {
      setCompensations(prev);
      reportError(err, 'Could not save the new allocation order.');
    }
  }, [compensations, reportError]);

  const deleteCompensation = useCallback(async (id: string) => {
    const prev = compensations;
    setCompensations((cs) => cs.filter((c) => c.id !== id)); // optimistic
    try {
      await apiClient.compensation.remove(id);
    } catch (err) {
      setCompensations(prev);
      reportError(err, 'Could not delete this compensation row.');
      throw err;
    }
  }, [compensations, reportError]);

  const toggleReceived = useCallback(async (compId: string, userId: string) => {
    const comp = compensations.find((c) => c.id === compId);
    const split = comp?.splits.find((s) => s.user === userId);
    const splitId = splitIdMap.current[`${compId}:${userId}`];
    if (!comp || !split || !splitId) {
      reportError(new Error('split-not-found'), 'Could not find this payment line.');
      return;
    }
    const next = !split.received;
    const today = new Date().toISOString().slice(0, 10);
    const prev = compensations;
    // optimistic
    setCompensations((cs) => cs.map((c) => (c.id !== compId ? c : {
      ...c,
      splits: c.splits.map((s) => (s.user === userId
        ? { ...s, received: next, receivedOn: next ? today : '' }
        : s)),
    })));
    try {
      await apiClient.compensation.setReceived(splitId, next);
    } catch (err) {
      setCompensations(prev);
      reportError(err, "You don't have permission to update this line.");
    }
  }, [compensations, reportError]);

  const createProjectFromBuilder = useCallback(async (
    input: CreateProjectInput, resumeProjectId?: string | null,
  ): Promise<{ projectId: string }> => {
    const { form, quotation } = input;
    const accepted = !!form.agreement?.accepted;
    const coIsName = form.coIsName
      ? String(form.coIsName).split(',').map((s: string) => s.trim()).filter(Boolean)
      : undefined;

    // The PM/owner (`pi`) and members must be ids that actually exist in the DB.
    // The roster is only trustworthy when it contains the authenticated user; for
    // non-admins `team` falls back to seeded fixture ids the server would reject,
    // so the only guaranteed-valid id is the current user.
    const rosterIsReal = team.some((u) => u.id === currentUserId);
    const pmId = rosterIsReal && team.some((u) => u.id === form.pm) ? form.pm : currentUserId;
    const validMembers = rosterIsReal
      ? (form.members || []).filter((id: string) => team.some((u) => u.id === id))
      : [];
    // Anyone assigned work in the Timeline step is auto-linked as a project
    // member (the server enforces the same invariant on every timeline write).
    const timelineAssignees = rosterIsReal
      ? (form.timelineItems || []).flatMap((t: any) => [t.user, ...(t.splits || []).map((s: any) => s.user)])
          .filter((id: any) => id && team.some((u) => u.id === id))
      : [];
    const members = Array.from(new Set([pmId, ...validMembers, ...timelineAssignees]));

    // Build the create/update payload from the builder form.
    const base = {
      code: form.code,
      title: form.title,
      shortTitle: form.shortTitle || String(form.title).slice(0, 24),
      design: form.design,
      area: form.area || '',
      sponsor: form.sponsor || '',
      pi: pmId,
      piName: form.piName || undefined,
      coIsName,
      status: accepted ? 'Protocol development' : 'Quotation preparation',
      startDate: form.startDate || '',
      endDate: form.endDate || '',
      sampleSize: Number(form.sampleSize) || 0,
      ethics: form.ethics || 'Not yet submitted',
      registry: form.registry || 'Pending',
      redcap: form.redcap || undefined,
      members,
      provisional: !accepted,
      // Canonical outcomes (single source of truth = Outcome & Analysis step). Feature 4.
      outcomes: (form.outcomes || []).map((o: any) => ({
        id: o.id,
        name: o.name || '',
        kind: o.kind,
        simpleUnits: o.simpleUnits,
        complexUnits: o.complexUnits,
        timepointWeeks: o.tpValue !== '' && o.tpValue != null ? Number(o.tpValue) : undefined,
        linkedVisitId: o.linkedVisitId || undefined,
        // Persist the rich {modifier,categories,prespecified} form so the canonical
        // Project.outcomes is NOT lossy (the engine folds subgroups by category count).
        subgroups: (o.subgroups || [])
          .filter((s: any) => (typeof s === 'string' ? s.trim() : (s?.modifier || '').trim()))
          .map((s: any) => (typeof s === 'string'
            ? { modifier: s, categories: 2, prespecified: true }
            : { modifier: s.modifier, categories: s.categories ?? 2, prespecified: s.prespecified !== false })),
        sensitivity: o.sensitivity || [],
      })),
      // Selected Data-Management quotation items (Feature 6).
      dmItems: Array.isArray(form.dmItems) ? form.dmItems : undefined,
      // Full builder snapshot so "Edit project" can reopen every step losslessly.
      // Mirrors the builder's serializeForm (Set fields → arrays for JSON).
      builderJson: JSON.stringify({
        ...form,
        // The evidence screenshot is uploaded as a project DOCUMENT below —
        // embedding its base64 here ballooned the payload past the API body
        // limit (HTTP 413 = "cannot activate"). Keep only its name + stamp.
        agreement: { ...(form.agreement || {}), evidenceImage: '' },
        labSelections: Object.fromEntries(Object.entries(form.labSelections || {}).map(([k, v]: any) => [k, [...(v || [])]])),
        nonLabSelected: [...(form.nonLabSelected || [])],
        nonLabAnalyzed: [...(form.nonLabAnalyzed || form.nonLabSelected || [])],
      }),
    };

    // A resumed edit must never REGRESS the project: an already-active project
    // keeps its real lifecycle status (re-saving used to force it back to
    // 'Protocol development' / provisional), and when the roster can't be
    // trusted (non-admin: GET /users 403s) we leave owner + members untouched
    // instead of collapsing the team to the current user.
    const saveBody: any = { ...base };
    if (resumeProjectId) {
      const existing = projects.find((p) => p.id === resumeProjectId);
      if (existing && !existing.provisional) {
        delete saveBody.status;
        delete saveBody.provisional;
      }
      if (!rosterIsReal) {
        delete saveBody.members;
        delete saveBody.pi;
      }
    }

    try {
      // 1) Create or update the project.
      let project: Project;
      if (resumeProjectId) {
        project = await apiClient.projects.update(resumeProjectId, saveBody as Partial<Project>);
        setProjects((ps) => ps.map((p) => (p.id === project.id ? project : p)));
      } else {
        project = await apiClient.projects.create(base as Partial<Project>);
        setProjects((ps) => [...ps, project]);
      }
      const pid = project.id;

      // 1.5) Upload the sponsor acceptance-evidence snapshot (captured in the builder)
      //      as a project document now that the project exists. Best-effort.
      const evidence = form.agreement?.evidenceImage;
      if (typeof evidence === 'string' && evidence.startsWith('data:')) {
        try {
          const blob = await (await fetch(evidence)).blob();
          // The filename extension MUST match the actual bytes — the server
          // magic-byte-sniffs uploads and 400s a .png name on JPEG content
          // (the builder compresses screenshots to JPEG).
          const wantExt = blob.type === 'image/jpeg' ? '.jpg' : blob.type === 'image/png' ? '.png' : '';
          const rawName = form.agreement?.evidenceName || 'sponsor-acceptance.png';
          const fname = wantExt && !rawName.toLowerCase().endsWith(wantExt) && !(wantExt === '.jpg' && rawName.toLowerCase().endsWith('.jpeg'))
            ? rawName.replace(/\.[^.]+$/, '') + wantExt
            : rawName;
          const file = new File([blob], fname, { type: blob.type || 'image/png' });
          await apiClient.documents.upload({ projectId: pid, type: 'acceptance-evidence', file });
        } catch { /* evidence upload is best-effort; the project is already created */ }
      }

      // 2) Persist the timeline rows the builder planned (Phase 2 only).
      // The rows actually saved this round — used to re-stamp the snapshot with
      // their REAL server ids (the form holds local 'tbN' ids on first save).
      let savedRows: TimelineItem[] | null = null;
      if (Array.isArray(form.timelineItems) && form.timelineItems.length > 0) {
        // One payload builder for create AND update. `omitOwner` leaves the
        // row's DB owner untouched (PATCH is partial) — used on updates when
        // the roster can't be validated, where the fallback below would
        // otherwise silently reassign every task to the editing user.
        const rowPayload = (t: any, ti: number, omitOwner = false) => {
          // Milestone AND phase-divider rows are timeline-only: no owner, no split, no compensation.
          const noWork = t.rowType === 'milestone' || t.rowType === 'phase';
          const payload: any = {
            name: t.name, category: t.category,
            rowType: t.rowType || 'task',
            order: ti,
            durationWeeks: t.durationWeeks,
            // Persist the %responsible split so compensation + per-person editing can link to it.
            splits: !noWork && Array.isArray(t.splits)
              ? t.splits.map((s: any) => ({ user: s.user, pct: Math.round(+s.pct || 0) })).filter((s: any) => s.user && (!rosterIsReal || team.some((u) => u.id === s.user)))
              : undefined,
            start: t.start ?? 0, end: t.end ?? 0,
            startDate: t.startDate || '', endDate: t.endDate || '',
            status: t.status || 'planned',
          };
          if (!omitOwner) {
            // Milestone / phase rows may have no responsible person (Feature 7). A Task
            // row may now also be UNASSIGNED ('') — we no longer dump a role-less
            // or unknown-id task onto the PM/admin. Only coerce to a validated id.
            payload.user = noWork ? '' : (() => {
              const cand = t.user || (t.splits && t.splits[0]?.user) || '';
              if (!cand) return '';                              // genuinely unassigned — leave blank
              if (!rosterIsReal) return cand;                    // can't validate roster — trust the builder
              return team.some((u) => u.id === cand) ? cand : ''; // unknown id → unassigned, not the PM
            })();
          }
          return payload;
        };

        if (resumeProjectId) {
          // DIFF the existing rows by id instead of delete-all + recreate:
          // updating a row in place keeps its id, so the Compensation
          // allocations that cascade-delete with their task survive the edit
          // (the old replace wiped every allocation and its received/paid state).
          const existing = timeline.filter((t) => t.project === pid);
          const existingIds = new Set(existing.map((t) => t.id));
          const formIds = new Set((form.timelineItems as any[]).map((t) => t.id));
          const removed = existing.filter((t) => !formIds.has(t.id));
          await Promise.all(removed.map((t) => apiClient.timeline.remove(t.id).catch(() => undefined)));
          const next: TimelineItem[] = [];
          for (let ti = 0; ti < form.timelineItems.length; ti++) {
            const t = form.timelineItems[ti];
            try {
              let row: TimelineItem;
              if (existingIds.has(t.id)) {
                const payload = rowPayload(t, ti, !rosterIsReal);
                // Don't REPLAY a status the user didn't change in the builder:
                // a colleague may have advanced it while the builder was open.
                // PATCH is partial — omitting the field keeps the server value.
                const hydrated = timeline.find((x) => x.id === t.id);
                if (hydrated && payload.status === hydrated.status) delete payload.status;
                row = await apiClient.timeline.update(t.id, payload);
              } else {
                row = await apiClient.timeline.create({ project: pid, ...rowPayload(t, ti) });
              }
              next.push(row);
            } catch { /* skip a bad row, keep going */ }
          }
          setTimeline((prev) => [...prev.filter((x) => x.project !== pid), ...next]);
          savedRows = next;
        } else {
        const created: TimelineItem[] = [];
        // Capture each task's %responsible split so we can auto-create the matching
        // compensation allocation after the tasks exist (Feature 12).
        const taskComp: { taskId: string; splits: Array<{ user: string; pct: number }> }[] = [];
        for (let ti = 0; ti < form.timelineItems.length; ti++) {
          const t = form.timelineItems[ti];
          const noWork = t.rowType === 'milestone' || t.rowType === 'phase';
          try {
            const row = await apiClient.timeline.create({ project: pid, ...rowPayload(t, ti) });
            created.push(row);
            if (!noWork && Array.isArray(t.splits) && t.splits.length) {
              taskComp.push({ taskId: row.id, splits: t.splits });
            }
          } catch { /* skip a bad row, keep going */ }
        }
        if (created.length) setTimeline((prev) => [...prev, ...created]);
        savedRows = created;

        // Auto-link compensation to the task's %responsible (Feature 12). Only on a
        // fresh activation (this is the non-resume branch) so we never duplicate
        // allocations. base starts at 0 — the PM enters the amount on the
        // Compensation page and each person's earning then follows the same split.
        if (taskComp.length) {
          for (const tc of taskComp) {
            const splits = (tc.splits || [])
              .map((s) => ({ user: s.user, pct: Math.round(+s.pct || 0) }))
              .filter((s) => s.user && (!rosterIsReal || team.some((u) => u.id === s.user)));
            const sum = splits.reduce((a, s) => a + s.pct, 0);
            if (!splits.length || sum !== 100) continue; // only link clean 100% splits
            try {
              const row = await apiClient.compensation.create({ taskId: tc.taskId, base: 0 });
              await apiClient.compensation.allocateSplits(row.id, { base: 0, splits });
            } catch { /* best-effort: the task still exists; PM can allocate manually */ }
          }
          // Bucket the freshly-created comp rows into the default phases (Phase 1/2/3 at
          // 10/40/50 + Other) with the canonical per-task %s. No money yet, so this
          // sets the STRUCTURE only — bases stay 0 until the PM enters trial receipts.
          try { await apiClient.compensation.groups.seedDefaults(pid); } catch { /* best-effort */ }
        }
        }
      }

      // 2.5) Re-stamp the snapshot with the REAL timeline row ids, so the NEXT
      //      resumed edit diffs rows in place (preserving compensation links)
      //      instead of treating every 'tbN'-id row as new and recreating it.
      if (savedRows && savedRows.length) {
        try {
          const bj = JSON.parse((base as any).builderJson);
          bj.timelineItems = savedRows.map((r: any, i: number) => ({
            id: r.id, name: r.name, category: r.category, rowType: r.rowType || 'task',
            order: r.order ?? i, durationWeeks: r.durationWeeks,
            splits: r.splits, user: r.user || '',
            startDate: r.startDate || '', endDate: r.endDate || '',
            start: r.start ?? 0, end: r.end ?? 0, status: r.status || 'planned',
          }));
          const restamped = await apiClient.projects.update(pid, { builderJson: JSON.stringify(bj) } as Partial<Project>);
          setProjects((ps) => ps.map((p) => (p.id === restamped.id ? restamped : p)));
        } catch { /* best-effort — the snapshot still works, just diffs less precisely */ }
      }

      // 3) Create the first quotation version from the builder form — but on a
      //    resumed edit NEVER mint a duplicate: the project's saved quotation(s)
      //    stay authoritative (re-saving used to append an auto-finalized
      //    'v1.0 (Draft)' at current library rates and demote the accepted one).
      const hasQuotationAlready = resumeProjectId ? quotations.some((q) => q.projectId === pid) : false;
      if (!hasQuotationAlready) {
      try {
        const created = await apiClient.quotations.create({
          projectId: pid,
          sections: quotation.sections.map((s: any, si: number) => ({
            id: `s${si}`,
            title: s.title,
            total: s.total,
            items: (s.items || []).map((it: any, ii: number) => ({
              id: `i${si}_${ii}`,
              code: it.code,
              name: it.name,
              qty: it.qty ?? 1,
              unit: it.unit || 'pkg',
              price: it.price ?? it.total ?? 0,
              total: it.total ?? 0,
            })),
          })),
          version: 'v1.0 (Draft)',
        });
        setQuotations((qs) => [...qs, created]);
        // If the sponsor accepted, finalise it as the project's active version.
        if (accepted) {
          try {
            const fin = await apiClient.quotations.lifecycle(created.id, 'Final');
            setQuotations((qs) => qs.map((q) => (q.id === fin.id ? fin : q)));
          } catch { /* lifecycle is best-effort */ }
        }
      } catch (qErr) {
        reportError(qErr, 'Project saved, but the quotation could not be created.');
      }
      } else if (accepted) {
        // The project already has its quotation(s); if acceptance was just
        // recorded, promote the chosen one to Final — mirroring what the
        // fresh-create path does for its new version.
        const projQs = quotations.filter((q) => q.projectId === pid);
        const target = projQs.find((q) => q.id === form.activeQuotationVersionId)
          || projQs.find((q) => q.status === 'Final')
          || projQs[0];
        if (target && target.status !== 'Final') {
          try {
            const fin = await apiClient.quotations.lifecycle(target.id, 'Final');
            setQuotations((qs) => qs.map((q) => (q.id === fin.id ? fin : q)));
          } catch { /* best-effort — quotation can be finalized manually */ }
        }
      }

      void refreshActivity(pid);
      return { projectId: pid };
    } catch (err) {
      reportError(err, 'Could not save the project.');
      throw err;
    }
  }, [timeline, team, projects, quotations, currentUserId, refreshActivity, reportError]);

  const proposeCatalogItem = useCallback(async (input: Parameters<typeof apiClient.catalog.propose>[0]) => {
    const row = await apiClient.catalog.propose(input);
    setCatalog((c) => [...c, row]);
    return row;
  }, []);
  const createCatalogItem = useCallback(async (input: Parameters<typeof apiClient.catalog.create>[0]) => {
    const row = await apiClient.catalog.create(input);
    setCatalog((c) => [...c, row]);
    return row;
  }, []);
  const approveCatalogItem = useCallback(async (id: string, edits?: Parameters<typeof apiClient.catalog.approve>[1]) => {
    const row = await apiClient.catalog.approve(id, edits);
    setCatalog((c) => c.map((x) => (x.id === id ? row : x)));
    return row;
  }, []);
  const rejectCatalogItem = useCallback(async (id: string, reviewNote?: string) => {
    const row = await apiClient.catalog.reject(id, reviewNote);
    setCatalog((c) => c.map((x) => (x.id === id ? row : x)));
    return row;
  }, []);
  const updateCatalogItem = useCallback(async (id: string, edits: Parameters<typeof apiClient.catalog.update>[1]) => {
    const row = await apiClient.catalog.update(id, edits);
    setCatalog((c) => c.map((x) => (x.id === id ? row : x)));
    return row;
  }, []);
  const removeCatalogItem = useCallback(async (id: string) => {
    await apiClient.catalog.remove(id);
    setCatalog((c) => c.filter((x) => x.id !== id));
  }, []);
  const createDmItem = useCallback(async (body: Partial<DataManagementItem>) => {
    const row = await apiClient.dmCatalog.create(body);
    setDmCatalog((c) => [...c, row]);
    return row;
  }, []);
  const updateDmItem = useCallback(async (id: string, body: Partial<DataManagementItem>) => {
    const row = await apiClient.dmCatalog.update(id, body);
    setDmCatalog((c) => c.map((x) => (x.id === id ? row : x)));
    return row;
  }, []);
  const removeDmItem = useCallback(async (id: string) => {
    await apiClient.dmCatalog.remove(id);
    setDmCatalog((c) => c.filter((x) => x.id !== id));
  }, []);

  const actions: DataActions = useMemo(() => ({
    createProjectFromBuilder, addTimelineItem, updateTimeline, reorderTimeline, deleteTimeline,
    updateTimelineStatus, updateProject, revertToDraft, deleteProject, restoreProject,
    addProjectMember, removeProjectMember,
    approveUser, rejectUser, uploadDocument, deleteDocument, createMeeting, updateMeeting,
    updateComp, reorderCompensation, deleteCompensation, toggleReceived,
    createCompGroup, updateCompGroup, deleteCompGroup, reorderCompGroups, moveCompToGroup, seedDefaultGroups,
    proposeCatalogItem, createCatalogItem, approveCatalogItem, rejectCatalogItem, updateCatalogItem, removeCatalogItem,
    createDmItem, updateDmItem, removeDmItem,
    reload: loadAll,
    setErrorSink: (fn) => { errorSink.current = fn; },
  }), [createProjectFromBuilder, addTimelineItem, updateTimeline, reorderTimeline, deleteTimeline,
    updateTimelineStatus, updateProject, revertToDraft, deleteProject, restoreProject,
    addProjectMember, removeProjectMember,
    approveUser, rejectUser, uploadDocument, deleteDocument, createMeeting, updateMeeting,
    updateComp, reorderCompensation, deleteCompensation, toggleReceived,
    createCompGroup, updateCompGroup, deleteCompGroup, reorderCompGroups, moveCompToGroup, seedDefaultGroups,
    proposeCatalogItem, createCatalogItem, approveCatalogItem, rejectCatalogItem, updateCatalogItem, removeCatalogItem,
    createDmItem, updateDmItem, removeDmItem, loadAll]);

  const value: AppDataValue = useMemo(
    () => ({ data, actions, ready, currentUserId, setCurrentUserId }),
    [data, actions, ready, currentUserId],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useAppData(): AppDataValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppData must be used within <AppDataProvider>');
  return v;
}

/** The domain data bag (drop-in replacement for the prototype's `window.HERO_DATA`). */
export function useData(): HeroData { return useAppData().data; }
/** Domain mutators. */
export function useDataActions(): DataActions { return useAppData().actions; }
/** Whether the first live load has completed. */
export function useDataReady(): boolean { return useAppData().ready; }
/** Acting user id + setter (used by the Tweaks "View as" control). */
export function useCurrentUserId(): [string, (id: string) => void] {
  const { currentUserId, setCurrentUserId } = useAppData();
  return [currentUserId, setCurrentUserId];
}
